import { appShell } from './app';

/**
 * Whether the Mac app's window shows in screen recordings, screen sharing and screenshots
 * (apps/apple/Bridge/WindowPrivacy.swift). Hidden by default; the settings sheet has the switch, since a window that
 * cannot be captured cannot be screenshotted by the person either.
 */
const HANDLER = 'bridgeWindow';

interface Handler {
  postMessage: (message: unknown) => Promise<unknown>;
}

function handler(): Handler | null {
  if (appShell()?.platform !== 'macos') return null;
  const webkit = (window as { webkit?: { messageHandlers?: Record<string, Handler | undefined> } }).webkit;
  return webkit?.messageHandlers?.[HANDLER] ?? null;
}

/** True where the shell offers the switch: the Mac app. */
export function windowPrivacyAvailable(): boolean {
  return handler() !== null;
}

function parse(value: unknown): boolean | null {
  if (typeof value !== 'object' || value === null) return null;
  const { hide } = value as Record<string, unknown>;
  return typeof hide === 'boolean' ? hide : null;
}

/** Whether the window is hidden from capture now, or null when the shell does not say. */
export async function readHideFromCapture(): Promise<boolean | null> {
  try {
    return parse(await handler()?.postMessage({ action: 'state' }));
  } catch {
    return null;
  }
}

/** Sets it, and answers how things stand after. */
export async function setHideFromCapture(hide: boolean): Promise<boolean | null> {
  try {
    return parse(await handler()?.postMessage({ action: 'set', hide }));
  } catch {
    return null;
  }
}
